import React, { useCallback, useEffect, useState } from "react";
import { Copy, Eye, KeyRound, Pencil, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  admin: "Admin",
  bdc_manager: "BDC Manager",
  bdc: "BDC Agent",
  dealer: "Dealer",
  customer: "Customer",
};

const ROLE_TONES = {
  admin: "bg-slate-900 text-white",
  bdc_manager: "bg-amber-200 text-amber-900",
  bdc: "bg-emerald-100 text-emerald-800",
  dealer: "bg-blue-100 text-blue-800",
  customer: "bg-slate-100 text-slate-700",
};

const AdminUsers = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [active, setActive] = useState("");
  const [q, setQ] = useState("");
  const [tick, setTick] = useState(0);

  const [editing, setEditing] = useState(null);
  const [resetInfo, setResetInfo] = useState(null);
  const { startImpersonation } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 200 };
      if (role) params.role = role;
      if (active) params.is_active = active === "yes";
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/users", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [role, active, q]);

  useEffect(() => { load(); }, [load, tick]);

  const onImpersonate = async (u) => {
    const res = await startImpersonation(u.id);
    if (res.ok) {
      toast.success(`Viewing as ${u.full_name || u.email}`);
      // Route the admin into that user's portal.
      const portalByRole = {
        customer: "/account",
        dealer: "/dealer",
        bdc: "/bdc",
        bdc_manager: "/bdc",
      };
      window.location.replace(portalByRole[res.targetRole] || "/");
    } else {
      toast.error(res.error);
    }
  };

  const onResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A temporary password will be shown ONCE.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetInfo(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deactivated");
      setTick((n) => n + 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminPage title="Users" subtitle="Unified user management across all roles" testId="admin-users-page">
      <div className="flex flex-wrap items-end gap-3 mb-4 rounded-2xl bg-white border border-slate-200 p-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Search</Label>
          <Input placeholder="name / email / phone" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="admin-users-search" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Role</Label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="admin-users-role-filter">
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="bdc_manager">BDC Manager</option>
            <option value="bdc">BDC Agent</option>
            <option value="dealer">Dealer</option>
            <option value="customer">Customer</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Status</Label>
          <select value={active} onChange={(e) => setActive(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="admin-users-active-filter">
            <option value="">All</option>
            <option value="yes">Active</option>
            <option value="no">Deactivated</option>
          </select>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-users-refresh">Refresh</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Name</th>
              <th className="text-left px-4 py-2 font-semibold">Email</th>
              <th className="text-left px-4 py-2 font-semibold">Role</th>
              <th className="text-left px-4 py-2 font-semibold">Dealer</th>
              <th className="text-left px-4 py-2 font-semibold">Status</th>
              <th className="text-right px-4 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">No users match.</td></tr>
            ) : items.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-2 font-medium text-slate-900">{u.full_name || "—"}</td>
                <td className="px-4 py-2 text-slate-700">{u.email}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_TONES[u.role] || "bg-slate-100 text-slate-700"}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-2 text-slate-600">{u.dealer_name || (u.role === "dealer" ? "—" : "")}</td>
                <td className="px-4 py-2">
                  {u.is_active === false ? (
                    <span className="inline-flex rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-[11px] font-semibold">Deactivated</span>
                  ) : (
                    <span className="inline-flex rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold">Active</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(u)} data-testid={`admin-user-edit-${u.id}`}><Pencil size={13} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onResetPassword(u)} data-testid={`admin-user-reset-${u.id}`}><KeyRound size={13} /></Button>
                    {u.role !== "admin" && u.is_active !== false && (
                      <Button size="sm" variant="ghost" onClick={() => onImpersonate(u)} data-testid={`admin-user-impersonate-${u.id}`}><Eye size={13} /></Button>
                    )}
                    {u.role !== "admin" && u.is_active !== false && (
                      <Button size="sm" variant="ghost" onClick={() => onDeactivate(u)} className="text-rose-600 hover:text-rose-700" data-testid={`admin-user-deactivate-${u.id}`}><Trash2 size={13} /></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100">Showing {items.length} of {total} users</div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent data-testid="admin-user-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update basic identity fields. Role changes require a separate flow.</DialogDescription>
          </DialogHeader>
          {editing && <EditUserForm user={editing} onDone={() => { setEditing(null); setTick((n) => n + 1); }} />}
        </DialogContent>
      </Dialog>

      {/* Reset password result */}
      <Dialog open={!!resetInfo} onOpenChange={(v) => { if (!v) setResetInfo(null); }}>
        <DialogContent data-testid="admin-user-reset-dialog">
          <DialogHeader>
            <DialogTitle>Password reset</DialogTitle>
            <DialogDescription>Share this password securely — it will not be shown again.</DialogDescription>
          </DialogHeader>
          {resetInfo && (
            <div className="rounded-lg bg-slate-100 p-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 mb-1">{resetInfo.email}</div>
                <code className="text-base font-mono text-slate-900" data-testid="admin-user-reset-password">{resetInfo.temp_password}</code>
              </div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(resetInfo.temp_password); toast.success("Copied"); }}>
                <Copy size={14} /> Copy
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setResetInfo(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

const EditUserForm = ({ user, onDone }) => {
  const [fullName, setFullName] = useState(user.full_name || "");
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone || "");
  const [isActive, setIsActive] = useState(user.is_active !== false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}`, {
        full_name: fullName, email, phone, is_active: isActive,
      });
      toast.success("User updated");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Full name</Label>
        <Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="admin-user-edit-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-user-edit-email" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Phone</Label>
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="admin-user-edit-phone" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="admin-user-edit-active" /> Active
      </label>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>Cancel</Button>
        <Button onClick={submit} disabled={saving} data-testid="admin-user-edit-submit">{saving ? "Saving…" : "Save"}</Button>
      </DialogFooter>
    </div>
  );
};

export default AdminUsers;
